/**
 * MiasmaSkillEffect — Poison tree evolution: drifting fog bank.
 *
 * Replaces Corrode. Spawns a circular `miasma` zone that drifts in the
 * player's facing direction at 1 tile/sec for 12 seconds, applying
 * `poison` DoT to everything it flows through. Enemies inside also
 * lose 50% of their detection radius (detectionReduction: 0.5).
 *
 * Does NOT inherit Settled's `cloudDurationMs` bonus (different scope);
 * Miasma queries `resolver.getAdditiveEffect('miasma', 'cloudDurationMs')`
 * if a future upgrade enhances its duration specifically.
 *
 * Intensity scaled by Potent Compound ('taint' scope) and Toxicologist
 * (tree-wide dotDamageMultiplier). Same resolver pattern as Corrode.
 *
 * Dead Air passive: `resolver.getTreePassiveEffect('poison', 'suppressesSound', 'max') > 0`
 * causes the zone to be created with `suppressesSound: true`.
 *
 * Cooldown tier: long (2000ms).
 *
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see poison-spec.md § Miasma (corrode_evolved)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { ZoneConfig, ActiveZone } from '../../../systems/ZoneSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface MiasmaDeps {
  readonly zoneSystem: {
    create(config: ZoneConfig): string;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
  readonly player: {
    readonly x: number;
    readonly y: number;
    readonly facingAngle: number; // radians
  };
  /** Required for Choking: apply post-exposure slow when entity exits after ≥ threshold. */
  readonly applySlowEffect?: (
    entityId: string,
    opts: { sourceSkillId: string; durationMs: number; intensity: number },
  ) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface MiasmaConfig {
  readonly cooldownMs: number;      // long tier = 2000ms
  readonly baseDurationMs: number;  // 12000ms
  readonly radiusTiles: number;     // 1.5 tiles (narrow drifting fog)
  readonly baseIntensity: number;   // 3
  readonly driftSpeedPxPerMs: number; // 1 tile/sec = 32px/1000ms = 0.032
  readonly detectionReduction: number; // 0.5 (50% reduction)
  readonly chokingThresholdMs: number; // 3000ms — minimum exposure for post-exit slow
}

const TILE_SIZE = 32; // px per tile

const DEFAULT_CONFIG: MiasmaConfig = {
  cooldownMs: 2000,
  baseDurationMs: 12000,
  radiusTiles: 1.5,
  baseIntensity: 3,
  driftSpeedPxPerMs: TILE_SIZE / 1000, // 1 tile/sec
  detectionReduction: 0.5,
  chokingThresholdMs: 3000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class MiasmaSkillEffect implements SkillEffect {
  readonly skillId = 'corrode_evolved';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: MiasmaDeps;
  private readonly config: MiasmaConfig;

  private _cooldownRemaining = 0;
  private _activationsCount = 0;
  private _zoneIds: string[] = [];

  constructor(deps: MiasmaDeps, config: Partial<MiasmaConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationsCount(): number { return this._activationsCount; }
  get zoneIds(): readonly string[] { return this._zoneIds; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const durationMs = this.config.baseDurationMs +
      this.deps.resolver.getAdditiveEffect('miasma', 'cloudDurationMs');

    const intensity = this.resolveIntensity();

    const facing = this.deps.player.facingAngle;
    const speed = this.config.driftSpeedPxPerMs;
    const velocity = {
      dx: Math.cos(facing) * speed,
      dy: Math.sin(facing) * speed,
    };

    // Dead Air passive: suppressesSound if tree passive active
    const suppressesSound =
      this.deps.resolver.getTreePassiveEffect('poison', 'suppressesSound', 'max') > 0;

    // Choking: post-exposure slow on exit if entity was inside for ≥ threshold
    const slowAmount = this.deps.resolver.getAdditiveEffect('miasma', 'postExposureSlowAmount');
    const onEntityExit =
      slowAmount > 0 && this.deps.applySlowEffect
        ? this._makeChokingExitCallback(slowAmount)
        : undefined;

    const zoneId = `miasma_cloud_${this._activationsCount}`;

    this.deps.zoneSystem.create({
      id: zoneId,
      type: 'miasma',
      position: { x: this.deps.player.x, y: this.deps.player.y },
      durationMs,
      sourceSkillId: 'miasma',
      shape: 'circle',
      radiusTiles: this.config.radiusTiles,
      dotDefId: 'poison',
      dotIntensity: intensity,
      tickIntervalMs: 1000,
      detectionReduction: this.config.detectionReduction,
      velocity,
      suppressesSound: suppressesSound || undefined,
      onEntityExit,
    });

    this._zoneIds.push(zoneId);
    this._cooldownRemaining = this.config.cooldownMs;
    this._activationsCount++;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Choking: build the onEntityExit callback embedded in each miasma zone config.
   * Captures slowAmount and resolves slowDurationMs at creation time so the
   * callback is a pure closure over stable values.
   */
  private _makeChokingExitCallback(
    slowAmount: number,
  ): (entityId: string, _zone: ActiveZone, dwellMs: number, _exitX: number, _exitY: number) => void {
    const slowDurationMs = this.deps.resolver.getAdditiveEffect('miasma', 'postExposureSlowDurationMs');
    const thresholdMs = this.config.chokingThresholdMs;
    const applySlowEffect = this.deps.applySlowEffect!;

    return (entityId: string, _zone, dwellMs: number) => {
      if (dwellMs >= thresholdMs) {
        applySlowEffect(entityId, {
          sourceSkillId: 'miasma',
          durationMs: slowDurationMs,
          intensity: slowAmount,
        });
      }
    };
  }

  private resolveIntensity(): number {
    const base = this.config.baseIntensity;
    const bonus = this.deps.resolver.getAdditiveEffect('taint', 'dotDamagePerTick');
    const multiplier = this.deps.resolver.getTreePassiveEffect(
      'poison', 'dotDamageMultiplier', 'product',
    );
    return (base + bonus) * multiplier;
  }
}
